<template>
  <div class="auth-main v1">
    <div class="auth-wrapper">
      <div class="auth-form">
        <div class="card my-5">
          <div class="card-body">
            <div class="text-center">
              <img src="/src/assets/images/logo/vesproc120.png" alt="VESPROC" class="img-fluid mb-3" />
              <h4 class="f-w-500 mb-1">{{ $t('auth.resetPassword.title') }}</h4>
              <p class="mb-3">{{ $t('auth.resetPassword.subtitle') }}</p>
            </div>
            
            <!-- Alert messages -->
            <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
              <i class="ph-duotone ph-warning-circle me-2"></i>
              {{ error }}
              <button type="button" class="btn-close" @click="error = null"></button>
            </div>
            <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
              <i class="ph-duotone ph-check-circle me-2"></i>
              {{ successMessage }}
              <button type="button" class="btn-close" @click="successMessage = null"></button>
            </div>
            
            <div v-if="!successMessage">
              <!-- New Password field -->
              <div class="mb-3">
                <label class="form-label text-muted">{{ $t('auth.resetPassword.newPassword') }}</label>
                <div class="position-relative">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control pe-5"
                    :class="{ 'is-invalid': v$.newPassword.$error }"
                    :placeholder="$t('auth.resetPassword.newPassword')"
                    v-model="newPassword"
                    @blur="v$.newPassword.$touch"
                  />
                  <button 
                    type="button" 
                    class="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                    @click="showPassword = !showPassword"
                    style="z-index: 10; border: none; background: none;"
                  >
                    <i :class="showPassword ? 'ph-duotone ph-eye-slash' : 'ph-duotone ph-eye'"></i>
                  </button>
                </div>
                <div v-if="v$.newPassword.$error" class="text-danger text-start small mt-1">
                  {{ v$.newPassword.$errors[0].$message }}
                </div>
                <!-- Password strength indicator -->
                <div v-if="newPassword" class="mt-2">
                  <div class="progress" style="height: 6px;">
                    <div 
                      class="progress-bar" 
                      :class="passwordStrengthClass"
                      :style="{ width: passwordStrengthPercent + '%' }"
                    ></div>
                  </div>
                  <small :class="passwordStrengthTextClass">
                    {{ passwordStrengthText }}
                  </small>
                  <!-- zxcvbn feedback for weak passwords -->
                  <div v-if="passwordStrength.score < 2 && passwordStrength.feedback.warning" class="mt-1">
                    <small class="text-warning">
                      <i class="ph-duotone ph-warning me-1"></i>
                      {{ passwordStrength.feedback.warning }}
                    </small>
                  </div>
                  <div v-if="passwordStrength.score < 2 && passwordStrength.feedback.suggestions.length > 0" class="mt-1">
                    <small class="text-muted">
                      <i class="ph-duotone ph-lightbulb me-1"></i>
                      {{ passwordStrength.feedback.suggestions[0] }}
                    </small>
                  </div>
                </div>
              </div>
              
              <!-- Confirm password field -->
              <div class="mb-4">
                <label class="form-label text-muted">{{ $t('auth.resetPassword.confirmPassword') }}</label>
                <div class="position-relative">
                  <input
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-control pe-5"
                    :class="{ 'is-invalid': v$.confirmPassword.$error }"
                    :placeholder="$t('auth.resetPassword.confirmPassword')"
                    v-model="confirmPassword"
                    @blur="v$.confirmPassword.$touch"
                  />
                  <button 
                    type="button" 
                    class="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3 text-muted"
                    @click="showConfirmPassword = !showConfirmPassword"
                    style="z-index: 10; border: none; background: none;"
                  >
                    <i :class="showConfirmPassword ? 'ph-duotone ph-eye-slash' : 'ph-duotone ph-eye'"></i>
                  </button>
                </div>
                <div v-if="v$.confirmPassword.$error" class="text-danger text-start small mt-1">
                  {{ v$.confirmPassword.$errors[0].$message }}
                </div>
              </div>
              
              <!-- Submit button -->
              <div class="d-grid">
                <button 
                  type="button" 
                  class="btn btn-primary btn-lg" 
                  @click="resetPassword" 
                  :disabled="isLoading || !token || !isFormValid"
                >
                  <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                  {{ $t('auth.resetPassword.resetButton') }}
                </button>
              </div>
            </div>
            
            <!-- Back to login link -->
            <div class="text-center mt-4">
              <p class="mb-0 text-muted">
                {{ $t('auth.resetPassword.backToLogin') }}
                <router-link to="/login" class="link-primary ms-1">{{ $t('auth.resetPassword.login') }}</router-link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <AuthFooter />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required, minLength, sameAs } from '@vuelidate/validators';
import authService from '@/services/authService';
import passwordStrengthService from '@/services/passwordStrengthService';
import AuthFooter from '@/components/auth/AuthFooter.vue';

export default {
  components: {
    AuthFooter
  },
  name: 'ResetPassword',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    
    const token = ref('');
    const newPassword = ref('');
    const confirmPassword = ref('');
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);
    const error = ref(null);
    const successMessage = ref(null);
    const isLoading = ref(false);

    // Validation rules
    const rules = {
      newPassword: { 
        required: required,
        minLength: minLength(6)
      },
      confirmPassword: {
        required: required,
        sameAsPassword: sameAs(newPassword)
      }
    };

    const v$ = useVuelidate(rules, { newPassword, confirmPassword });

    // Computed properties
    const isFormValid = computed(() => {
      return newPassword.value.length >= 6 && 
             newPassword.value === confirmPassword.value &&
             passwordStrengthService.meetsMinimumStrength(passwordStrength.value.score, 2);
    });

    // Advanced password strength calculation using zxcvbn-ts
    const passwordStrength = computed(() => {
      const pwd = newPassword.value;
      if (!pwd) {
        return {
          score: 0,
          strength: 'veryWeak',
          text: '',
          class: 'bg-secondary',
          textClass: 'text-muted',
          percentage: 0,
          feedback: { warning: '', suggestions: [] }
        };
      }
      
      // Prepare user inputs to penalize personal info in passwords
      const userInputs = [];
      
      // Add common site-specific terms
      userInputs.push('nozzle', 'supplier', 'portal', 'password', 'reset');
      
      // Evaluate password using zxcvbn-ts
      const evaluation = passwordStrengthService.evaluatePassword(pwd, userInputs);
      
      return {
        score: evaluation.score,
        strength: evaluation.strength,
        text: passwordStrengthService.getLocalizedStrength(evaluation.strength, t),
        class: passwordStrengthService.getStrengthClass(evaluation.score),
        textClass: passwordStrengthService.getStrengthTextClass(evaluation.score),
        percentage: passwordStrengthService.getStrengthPercentage(evaluation.score),
        feedback: evaluation.feedback,
        guesses: evaluation.guesses,
        crackTime: evaluation.crackTimesDisplay,
        calcTime: evaluation.calculation_time
      };
    });

    const passwordStrengthPercent = computed(() => passwordStrength.value.percentage);
    const passwordStrengthClass = computed(() => passwordStrength.value.class);
    const passwordStrengthText = computed(() => passwordStrength.value.text);
    const passwordStrengthTextClass = computed(() => passwordStrength.value.textClass);

    // Extract token from URL on component mount
    onMounted(() => {
      token.value = route.query.token || '';
      
      if (!token.value) {
        error.value = t('auth.resetPassword.invalidToken');
      }
    });

    const resetPassword = async () => {
      error.value = null;
      
      // Validate form
      const isFormValid = await v$.value.$validate();
      if (!isFormValid) {
        error.value = t('auth.resetPassword.validationError');
        return;
      }
      
      if (!token.value) {
        error.value = t('auth.resetPassword.invalidToken');
        return;
      }
      
      isLoading.value = true;
      
      try {
        const resetData = {
          token: token.value,
          newPassword: newPassword.value,
          confirmPassword: confirmPassword.value
        };
        
        const result = await authService.resetPassword(resetData);
        
        // Check if result is boolean or object
        const isSuccess = typeof result === 'boolean' ? result : result.success !== false;
        
        if (isSuccess) {
          successMessage.value = t('auth.resetPassword.success');
          
          // Reset form
          newPassword.value = '';
          confirmPassword.value = '';
          v$.value.$reset();
          
          // Redirect to login after 3 seconds
          setTimeout(() => {
            router.push('/login');
          }, 3000);
        } else {
          error.value = t('auth.resetPassword.invalidToken');
        }
      } catch (err) {
        // Handle API errors
        if (err.response?.status === 400) {
          if (err.response.data?.errorMessage?.includes('token') || err.response.data?.message?.includes('token')) {
            error.value = t('auth.resetPassword.invalidToken');
          } else {
            error.value = err.response.data?.errorMessage || err.response.data?.message || t('auth.resetPassword.error');
          }
        } else if (err.response?.status === 422) {
          error.value = err.response.data?.errorMessage || err.response.data?.message || t('auth.resetPassword.validationError');
        } else {
          error.value = t('auth.resetPassword.networkError');
        }
        console.error('Reset password error:', err);
      } finally {
        isLoading.value = false;
      }
    };

    return {
      token,
      newPassword,
      confirmPassword,
      showPassword,
      showConfirmPassword,
      error,
      successMessage,
      isLoading,
      resetPassword,
      v$,
      isFormValid,
      passwordStrength,
      passwordStrengthPercent,
      passwordStrengthClass,
      passwordStrengthText,
      passwordStrengthTextClass
    };
  }
};
</script>

<style scoped>
.auth-main.v1 {
  background-color: #f2f2f2;
  background-image: url('/src/assets/images/logo/worldmap.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.progress {
  transition: all 0.3s ease;
  border-radius: 3px;
}

.btn-link {
  text-decoration: none !important;
}

.btn-link:hover {
  color: var(--bs-primary) !important;
}

.position-relative .btn-link {
  padding: 0;
  width: 40px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alert {
  border-radius: 0.5rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.form-control {
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.form-control:focus {
  box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25);
}

.card {
  border-radius: 1rem;
  border: none;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.btn-lg {
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
}

/* Increase form width to match login/register */
.auth-form {
  max-width: 800px;
  min-width: 400px !important;
  width: 100%;
  margin: 0 auto;
}

.auth-form .card {
  width: 100%;
}
</style> 